/**
 * Every number that defines how the character feels, in one place.
 *
 * These are the values from docs/02-movement.md, and that document is the
 * rationale for each. They are mutable at runtime so the F2 debug panel can
 * tune them live — feel is found by playing, not by arithmetic, and a tuning
 * loop that requires a rebuild is a tuning loop nobody uses.
 *
 * Units: 1 cell = 1 pixel = 1 unit. Speeds per second. Times in seconds
 * (the doc quotes ms; converted here once, at the boundary).
 */

/**
 * How the dash spends its duration — issue #177/docs/02 §row "Chrono-monk".
 * `'translate'` is every body but Chrono-monk: forced velocity along the aimed
 * direction for `dashDuration`, exactly what shipped before this type existed.
 * `'rewind'` snaps the mover back to where it stood `dashRewindSeconds` ago
 * instead, and holds still (zero forced velocity) for the rest of the window —
 * "escapes, never advances," per the card.
 */
export type DashMode = 'translate' | 'rewind';

/**
 * How a body gets vertical once it has left the ground — issue #171, split
 * from #159. `'double-jump'` is every body but three: the ordinary kit
 * (jump, wall-jump, and whatever air dashes Airstep grants — there is no
 * separate "press jump again in the air" verb this replaces, despite the
 * name; `'double-jump'` names the *baseline*, not a mechanic `stepMover`
 * overrides). `'hover-thrust'` and `'flight'` are sustained, fuel-metered
 * lift instead: holding jump in the air, with fuel left, replaces free-fall
 * with lift for as long as the fuel lasts. `'none'` disables vertical
 * recovery entirely (authored on nothing today, reserved for a body that
 * should have neither).
 */
export type VerticalMode = 'double-jump' | 'hover-thrust' | 'flight' | 'none';

export interface MovementTuning {
  // horizontal
  maxRunSpeed: number;
  groundAccel: number;
  groundDecel: number;
  turnAccelBonus: number;
  airAccel: number;
  airDecel: number;
  airTurnBonus: number;
  /**
   * Berserker — issue #178, split from #159. Extra fraction of `maxRunSpeed`
   * per fraction of HP missing, read live off the mover's own HP each substep
   * (`applyHorizontal`) rather than baked into a static tuning number the way
   * every other modifier here is: "how fast" changes every hit, so it cannot
   * be resolved once at archetype-select time the way `groundFrictionMul` is.
   * 0 for every other archetype, so this line of `applyHorizontal` is a no-op
   * for them.
   */
  speedPerMissingHp: number;

  // jump
  jumpHeight: number;
  jumpApexTime: number;
  riseGravity: number;
  fallGravity: number;
  apexGravity: number;
  apexThreshold: number;
  releaseCutMultiplier: number;
  maxFallSpeed: number;
  fastFallSpeed: number;

  // forgiveness
  coyoteTime: number;
  jumpBuffer: number;
  wallCoyote: number;
  landingLockout: number;
  /** Horizontal nudge when the head clips a ceiling corner, in units. */
  cornerCorrection: number;
  /** Vertical nudge when the feet just miss a ledge, in units. */
  ledgeSnap: number;

  // wall
  wallSlideSpeed: number;
  wallSlideGrace: number;
  wallJumpImpulseX: number;
  wallJumpImpulseY: number;
  wallJumpInputLock: number;
  sameWallJumpPenalty: number;
  clingClimbSpeed: number;

  // dash
  dashSpeed: number;
  dashDuration: number;
  dashCooldown: number;
  dashGravityScale: number;
  dashPostGravityScale: number;
  dashPostGravityTime: number;
  dashIFrames: number;
  dashMomentumKeep: number;
  dashMode: DashMode;
  /** How far back `'rewind'` snaps to. Inert under `'translate'`. */
  dashRewindSeconds: number;
  /**
   * Air dashes *beyond* the one Airstep itself grants. Airstep is the source of
   * the budget (docs/02 §6 rule 1 — the pickup must do something in an empty
   * room); this is the archetype delta stacked on top of it.
   */
  maxAirDashes: number;

  // fluid
  fluidMaxSpeedScale: number;
  fluidAccelScale: number;
  fluidJumpScale: number;
  /**
   * Added to the density-ratio buoyancy so a body at exactly the medium's
   * density still sinks. Pure physics gives a neutrally buoyant player zero net
   * gravity, and with no swim-down input that is water you can never descend
   * in — a route lost, not a feature gained.
   */
  fluidSinkBias: number;

  // feedback
  landSoftThreshold: number;
  landHardThreshold: number;
  /** Hitstop on a heavy landing, seconds (docs/02 §2.4). */
  landHitstop: number;
  pogoImpulse: number;

  /**
   * Issue #174, split from #159. Loose cells (`Category.LOOSE` — sand, ash,
   * gravel, snow) collide like rock for every mover except the archetypes
   * that author this true (Breaker, Spectral entity). `solidIn` is where it
   * takes effect; this field only carries it from the archetype to the
   * mover, the same relay `groundFrictionMul` (#176) is for `groundDecel`.
   */
  passesLooseSolids: boolean;

  /**
   * Issue #179, split from #159 (itself split from #174, which shipped
   * `passesLooseSolids` alone). Narrowest gap, in cells, this body can
   * squeeze through — base 10 matches the Duelist's own 10-wide capsule
   * (`halfW` 5 → width 10), so the base archetype's collision box is
   * already the limit and this field is a no-op for it. Semi-liquid
   * mutant (3) and Modular drone swarm (1) are authored *below* their own
   * body width on purpose (docs/02 §3.2's "squeezes through 3-cell gaps",
   * "passes through 1-cell gaps") — the whole point is passing a corridor
   * the body's own AABB would otherwise refuse, not an additional refusal
   * on top of one already narrower than the box.
   */
  minGapCells: number;

  /**
   * Issue #180, split from #159 (itself split from #174). Downhill accel
   * bonus / uphill drag, applied against `w.groundSlope[i]` — a *live*
   * per-substep read, sampled fresh every `stepBody` call
   * (`body.ts`'s `groundSlopeAt`), the same "cannot be resolved once at
   * archetype-select time" reasoning `speedPerMissingHp`'s own comment
   * gives for reading live HP instead of a static coefficient. This field
   * itself is the static per-archetype coefficient — 0.65 for Semi-liquid
   * mutant ("flows downhill; slow uphill" — docs/02 §3.2), 0 for
   * everything else. `applyHorizontal` is the one reader.
   */
  slopeBias: number;

  /**
   * Issue #171, split from #159. How this body gets vertical once airborne —
   * `'double-jump'` for everyone but Techno-knight/Modular drone swarm
   * (`'hover-thrust'`) and Floating relic (`'flight'`). `stepMover`'s state
   * selection is the one reader.
   */
  verticalMode: VerticalMode;
  /**
   * Seconds of sustained lift `'flight'` mode grants before it runs dry —
   * 3.6 for Floating relic, 1.8 for Modular drone swarm, 0 (no fuel, so
   * `'flight'` can never actually engage) for every `'hover-thrust'` or
   * `'double-jump'` body. `flightCapacity` reads whichever of this and
   * `thermalSeconds` matches `verticalMode`.
   */
  flightSeconds: number;
  /**
   * Seconds of `'hover-thrust'` before it overheats — 2.2 for Techno-knight,
   * 0 for Modular drone swarm (its hover-thrust is fuelled by
   * `flightSeconds` instead, and carries no overheat/vent-in-water tension
   * because it has none to vent). 0 also means "no thermal model" — the
   * water-vents/water-shorts tension `applyVerticalMode` applies only fires
   * when this is nonzero, so Modular drone swarm's hover is unaffected by
   * water the way Techno-knight's is.
   */
  thermalSeconds: number;

  /**
   * Issue #186. Segmented biomech only: "anchor the tail and stretch, reach
   * replaces jump distance, and it is aimable" (the archetype's own `special`
   * text, `meta/archetypes.ts`). `handleJump`'s grounded branch reads this —
   * true replaces the ordinary straight-up launch with one aimed by the same
   * 8-way stick read `handleDash` already uses, at the same total speed the
   * vertical jump would have had. False (every other body) reproduces the
   * ordinary jump exactly: `handleJump`'s neutral-aim fallback *is* a bare
   * vertical launch, on purpose, so this field changes nothing for a body
   * that never sets it.
   */
  tailAnchorReach: boolean;
}

/** The shipping values. Derived quantities are computed, not hand-entered. */
export function defaultTuning(): MovementTuning {
  const jumpHeight = 54;
  const jumpApexTime = 0.34;
  // v0 and g that produce exactly `jumpHeight` in exactly `jumpApexTime`.
  const riseGravity = (2 * jumpHeight) / (jumpApexTime * jumpApexTime);

  return {
    maxRunSpeed: 145,
    groundAccel: 1400,
    groundDecel: 1900,
    turnAccelBonus: 2.1,
    airAccel: 900,
    // Lowered from the doc's 420 after testing: at 420 you lose 58% of top
    // speed in 200 ms of neutral stick, which reads as air *braking*, not as
    // "momentum is preserved". 240 keeps ~2/3 of the speed across a full jump
    // arc, which is what the pillar actually asks for.
    airDecel: 240,
    airTurnBonus: 1.8,
    speedPerMissingHp: 0,

    jumpHeight,
    jumpApexTime,
    riseGravity,
    // Coming down harder than you went up is the whole cure for "floaty".
    fallGravity: riseGravity * 1.6,
    apexGravity: riseGravity * 0.6,
    apexThreshold: 34,
    releaseCutMultiplier: 0.42,
    maxFallSpeed: 620,
    fastFallSpeed: 850,

    coyoteTime: 0.096,
    jumpBuffer: 0.116,
    wallCoyote: 0.13,
    landingLockout: 0,
    cornerCorrection: 4,
    ledgeSnap: 3,

    wallSlideSpeed: 118,
    wallSlideGrace: 0.14,
    wallJumpImpulseX: 172,
    wallJumpImpulseY: -400,
    wallJumpInputLock: 0.1,
    sameWallJumpPenalty: 0.72,
    clingClimbSpeed: 62,

    dashSpeed: 430,
    dashDuration: 0.145,
    dashCooldown: 0.32,
    dashGravityScale: 0,
    dashPostGravityScale: 0.35,
    dashPostGravityTime: 0.08,
    dashIFrames: 0.09,
    dashMomentumKeep: 0.55,
    dashMode: 'translate',
    // docs/02 §row "Chrono-monk": "Dash rewinds position 0.4 s".
    dashRewindSeconds: 0.4,
    maxAirDashes: 0,

    fluidMaxSpeedScale: 0.55,
    fluidAccelScale: 0.4,
    fluidJumpScale: 0.7,
    // Tuned to land water (density 1000, player mass 1) on a 0.22 gravity scale
    // — the value the old binary dense/light branch used for it, so the feel of
    // the most common liquid in the game survives the consolidation.
    fluidSinkBias: 0.22,

    landSoftThreshold: 300,
    landHardThreshold: 520,
    landHitstop: 0.04,
    pogoImpulse: 320,

    passesLooseSolids: false,
    minGapCells: 10,
    slopeBias: 0,
    verticalMode: 'double-jump',
    flightSeconds: 0,
    thermalSeconds: 0,
    tailAnchorReach: false,
  };
}

/** Jump velocity that reaches `jumpHeight` under `riseGravity`. */
export function jumpVelocity(t: MovementTuning): number {
  return -Math.sqrt(2 * t.riseGravity * t.jumpHeight);
}

/**
 * How hard a liquid lets you push against it, as a multiple of `maxRunSpeed`.
 *
 * Water is 1× by construction. It lives here, in the one place both the
 * controller and the abilities may import, because the two used to compute it
 * separately: `abilities.ts` scaled Fluid Jet's cap by density while
 * `applyVertical` clamped the swim speed to a flat ±`maxRunSpeed`, and the two
 * agreed only at water. In mercury that made a sideways jet fast and an upward
 * one deleted on the next substep — the same ability behaving differently by
 * axis, against docs/02 §3 row 7's promise that the material table is the skill
 * ceiling.
 */
export function fluidSpeedScale(density: number): number {
  return Math.max(0.25, Math.min(2.4, density / 1000));
}

/**
 * Per-archetype movement deltas.
 *
 * Declared *structurally* rather than imported from `meta/archetypes.ts`: TS is
 * structural, so the meta layer's richer `MovementModifiers` satisfies this
 * without `entity/` taking a dependency on `meta/`. The controller stays
 * ignorant of where its numbers came from, which is what lets archetypes,
 * status effects and future mutators all feed the same path.
 *
 * Everything is multiplicative on the base package so a body genuinely changes
 * the platforming problem — docs/02 §3.2: a body is not a skin.
 */
export interface MovementModifiers {
  maxRunSpeedMul?: number;
  groundAccelMul?: number;
  airAccelMul?: number;
  turnAccelMul?: number;
  massMul?: number;
  /** Not multiplicative like its neighbours above — see `MovementTuning`'s own field. */
  speedPerMissingHp?: number;
  groundFrictionMul?: number;

  jumpHeightMul?: number;
  riseGravityMul?: number;
  fallGravityMul?: number;
  maxFallSpeedMul?: number;
  /**
   * There is deliberately no `fallDamageMul` — issue #97.
   *
   * One was declared here and authored on eight archetypes with values from 0
   * to 1.3, a spread that plainly encoded intent: 0 reads as immune, 1.3 as
   * fragile. Nothing ever read it, because **there is no fall damage in this
   * game**. `handleContacts` turns `landingSpeed` into squash, hitstop and a
   * terrain crack, never into HP.
   *
   * Deleted rather than implemented. Falling costs nothing today, and that is
   * what the movement the player calls "fantastic" was tuned against; adding a
   * damage source underneath it is a design change, not the completion of an
   * unfinished one. If fall damage is ever wanted it deserves fresh values
   * chosen against a system that exists, not eight numbers picked for one that
   * never did.
   */

  dashSpeedMul?: number;
  dashDurationMul?: number;
  dashCooldownMul?: number;
  airDashBonus?: number;
  dashMode?: DashMode;

  wallSlideSpeedMul?: number;
  canWallSlide?: boolean;
  canWallCling?: boolean;

  buoyancyMul?: number;
  viscosityImmunity?: number;

  passesLooseSolids?: boolean;

  /** Not multiplicative — see `MovementTuning`'s own field. */
  minGapCells?: number;

  /** Not multiplicative — see `MovementTuning`'s own field. */
  slopeBias?: number;

  /** Not multiplicative — see `MovementTuning`'s own field. */
  verticalMode?: VerticalMode;
  /** Not multiplicative — see `MovementTuning`'s own field. */
  flightSeconds?: number;
  /** Not multiplicative — see `MovementTuning`'s own field. */
  thermalSeconds?: number;
  /** Not multiplicative — see `MovementTuning`'s own field. */
  tailAnchorReach?: boolean;
}

export function applyModifiers(
  base: MovementTuning,
  m: Readonly<MovementModifiers>,
): MovementTuning {
  const t = { ...base };
  const mul = (v: number, k: number | undefined) => (k === undefined ? v : v * k);

  t.maxRunSpeed = mul(t.maxRunSpeed, m.maxRunSpeedMul);
  t.groundAccel = mul(t.groundAccel, m.groundAccelMul);
  t.airAccel = mul(t.airAccel, m.airAccelMul);
  t.turnAccelBonus = mul(t.turnAccelBonus, m.turnAccelMul);
  t.airTurnBonus = mul(t.airTurnBonus, m.turnAccelMul);
  // `groundDecel` is what `applyHorizontal` (movement.ts) actually reads as
  // ground friction — issue #176. The field existed on this interface with no
  // effect at all: declared, authored per-archetype, never multiplied in.
  t.groundDecel = mul(t.groundDecel, m.groundFrictionMul);
  t.speedPerMissingHp = m.speedPerMissingHp ?? t.speedPerMissingHp;

  t.jumpHeight = mul(t.jumpHeight, m.jumpHeightMul);
  t.riseGravity = mul(t.riseGravity, m.riseGravityMul);
  t.fallGravity = mul(t.fallGravity, m.fallGravityMul);
  // Apex gravity tracks rise gravity: it is defined as a fraction of it, and
  // scaling one without the other silently changes the hang time that the
  // whole jump arc is tuned around.
  t.apexGravity = mul(t.apexGravity, m.riseGravityMul);
  t.maxFallSpeed = mul(t.maxFallSpeed, m.maxFallSpeedMul);
  t.fastFallSpeed = mul(t.fastFallSpeed, m.maxFallSpeedMul);

  t.dashSpeed = mul(t.dashSpeed, m.dashSpeedMul);
  t.dashDuration = mul(t.dashDuration, m.dashDurationMul);
  t.dashCooldown = mul(t.dashCooldown, m.dashCooldownMul);
  t.maxAirDashes = base.maxAirDashes + (m.airDashBonus ?? 0);
  t.dashMode = m.dashMode ?? t.dashMode;

  t.wallSlideSpeed = mul(t.wallSlideSpeed, m.wallSlideSpeedMul);
  if (m.canWallSlide === false) {
    // Berserkers cannot wall-slide. Expressed as an unreachable slide speed
    // rather than a branch in the controller, so there is one fewer state.
    t.wallSlideSpeed = t.maxFallSpeed;
    t.wallSlideGrace = 0;
  }
  // `canWallCling` is deliberately *not* handled here. It is descriptive
  // metadata for the run-setup screen; the actual grant is the archetype's
  // `innateAbilities: ['cling']`, which game.ts translates into `Ability.CLING`
  // on the mover. There used to be a `× 1` multiply here that looked like the
  // grant and was a no-op.

  t.fluidMaxSpeedScale = lerpToward(t.fluidMaxSpeedScale, 1, m.viscosityImmunity ?? 0);
  t.fluidAccelScale = lerpToward(t.fluidAccelScale, 1, m.viscosityImmunity ?? 0);

  t.passesLooseSolids = m.passesLooseSolids ?? t.passesLooseSolids;
  t.minGapCells = m.minGapCells ?? t.minGapCells;
  t.slopeBias = m.slopeBias ?? t.slopeBias;
  t.verticalMode = m.verticalMode ?? t.verticalMode;
  t.flightSeconds = m.flightSeconds ?? t.flightSeconds;
  t.thermalSeconds = m.thermalSeconds ?? t.thermalSeconds;
  t.tailAnchorReach = m.tailAnchorReach ?? t.tailAnchorReach;

  return t;
}

function lerpToward(v: number, target: number, t: number): number {
  return v + (target - v) * Math.max(0, Math.min(1, t));
}
